import { memo } from 'react';
import { Handle, Position, NodeResizer } from '@xyflow/react';
import { Shield } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { Domain } from '@/types/assets';

interface ZoneGroupNodeData {
  id: string;
  label: string;
  domain: Domain;
  purdueLevel?: string;
  cidrs?: string[];
  vlanId?: number;
}

interface ZoneGroupNodeProps {
  data: ZoneGroupNodeData;
  selected: boolean;
}

const getDomainTheme = (domain: string) => {
  switch (domain) {
    case 'IT':
      return {
        border: 'border-blue-500/70 dark:border-blue-400/70',
        bg: 'bg-blue-50/50 dark:bg-blue-950/50',
        header: 'bg-blue-100/60 dark:bg-blue-900/50 border-blue-300/40 dark:border-blue-600/40',
        icon: 'text-blue-600 dark:text-blue-400',
        handle: 'border-blue-500 bg-blue-200 dark:bg-blue-700'
      };
    case 'OT':
      return {
        border: 'border-orange-500/70 dark:border-orange-400/70',
        bg: 'bg-orange-50/50 dark:bg-orange-950/50',
        header: 'bg-orange-100/60 dark:bg-orange-900/50 border-orange-300/40 dark:border-orange-600/40',
        icon: 'text-orange-600 dark:text-orange-400',
        handle: 'border-orange-500 bg-orange-200 dark:bg-orange-700'
      };
    case 'Cloud':
      return {
        border: 'border-purple-500/70 dark:border-purple-400/70',
        bg: 'bg-purple-50/50 dark:bg-purple-950/50',
        header: 'bg-purple-100/60 dark:bg-purple-900/50 border-purple-300/40 dark:border-purple-600/40',
        icon: 'text-purple-600 dark:text-purple-400',
        handle: 'border-purple-500 bg-purple-200 dark:bg-purple-700'
      };
    default:
      return {
        border: 'border-gray-400/70 dark:border-gray-500/70',
        bg: 'bg-gray-50/50 dark:bg-gray-900/50',
        header: 'bg-gray-100/60 dark:bg-gray-800/50 border-gray-300/40 dark:border-gray-600/40',
        icon: 'text-gray-600 dark:text-gray-400',
        handle: 'border-gray-500 bg-gray-200 dark:bg-gray-700'
      };
  }
};

export const ZoneGroupNode = memo(({ data, selected }: ZoneGroupNodeProps) => {
  const theme = getDomainTheme(data.domain);
  
  return (
    <>
      <NodeResizer
        minWidth={300}
        minHeight={200}
        isVisible={selected}
        lineClassName="border-primary border-2"
        handleClassName="h-6 w-6 bg-primary rounded-sm border-2 border-background cursor-nwse-resize"
      />
      <div className={`w-full h-full rounded-lg border-2 ${theme.border} ${theme.bg} backdrop-blur-sm`}>
        <div className={`flex items-center gap-2 px-3 py-1.5 ${theme.header} rounded-t-lg border-b`}>
          <Shield className={`h-4 w-4 ${theme.icon}`} />
          <div className="flex-1 min-w-0">
            <div className="font-medium text-sm truncate">{data.label}</div>
            {(data.cidrs?.length || data.vlanId) && (
              <div className="text-xs text-muted-foreground truncate">
                {data.vlanId && <span>VLAN {data.vlanId}</span>}
                {data.vlanId && data.cidrs?.length ? ' · ' : ''}
                {data.cidrs?.length ? data.cidrs[0] : ''}
                {data.cidrs && data.cidrs.length > 1 && ` +${data.cidrs.length - 1}`}
              </div>
            )}
          </div>
          <Badge variant="outline" className={`${theme.icon} border-current/50`}>
            {data.domain}
          </Badge>
          {data.purdueLevel && (
            <Badge variant="outline" className="text-xs">
              {data.purdueLevel}
            </Badge>
          )}
        </div>
      </div>

      <Handle id="top" type="source" position={Position.Top} isConnectableStart={true} isConnectableEnd={true} className={`w-3 h-3 border-2 ${theme.handle}`} />
      <Handle id="right" type="source" position={Position.Right} isConnectableStart={true} isConnectableEnd={true} className={`w-3 h-3 border-2 ${theme.handle}`} />
      <Handle id="bottom" type="source" position={Position.Bottom} isConnectableStart={true} isConnectableEnd={true} className={`w-3 h-3 border-2 ${theme.handle}`} />
      <Handle id="left" type="source" position={Position.Left} isConnectableStart={true} isConnectableEnd={true} className={`w-3 h-3 border-2 ${theme.handle}`} />
    </>
  );
});

ZoneGroupNode.displayName = 'ZoneGroupNode';
